/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl-next';

import { jsx } from '@atlaskit/css';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { useElementBreakpoints } from '../../hooks/useElementBreakpoints';

import {
	ErrorButton,
	ErrorButtonContainer,
	ErrorFloatingContainer,
	ErrorMessage,
} from './error-screen-parts';
import messages from './messages';

type Props = {
	appearance?: EditorAppearance;
	onCancel: () => void;
	onRetry?: () => void;
	showRetryButton?: boolean;
	text: React.ReactNode;
};

export const ErrorScreen = ({
	onCancel,
	onRetry,
	text,
	showRetryButton = true,
	appearance,
}: Props) => {
	const { formatMessage } = useIntl();
	const [setBreakpointsElement, { breakpoint }] = useElementBreakpoints();

	return (
		<ErrorFloatingContainer
			ref={setBreakpointsElement}
			breakpoint={breakpoint}
			appearance={appearance}
		>
			<VisuallyHidden>{formatMessage(messages.ariaErrorTitle)}</VisuallyHidden>
			<ErrorMessage breakpoint={breakpoint}>{text}</ErrorMessage>
			<ErrorButtonContainer>
				<ErrorButton
					autoFocus
					testId="cancel-button"
					onClick={onCancel}
					appearance={onRetry && showRetryButton ? 'subtle' : 'primary'}
				>
					{formatMessage(fg('aifc_create_enabled') ? messages.closeLabel : messages.cancelLabel)}
				</ErrorButton>
				{onRetry && showRetryButton && (
					<ErrorButton testId={'retry'} onClick={onRetry} appearance="primary">
						{formatMessage(messages.retryLabel)}
					</ErrorButton>
				)}
			</ErrorButtonContainer>
		</ErrorFloatingContainer>
	);
};
